import * as React from "react"

import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@coval/ui"
import { Check, Tag } from "lucide-react"

import { useOrganizationTags } from "../routes/useOrganizationTags"
import { buildTagFilterItems } from "../utils/tagCatalog"
import { normalizeTags } from "../utils/tagNormalization"

interface TagFilterSubmenuProps {
  selectedTags: string[]
  onSelectedTagsChange: (tags: string[]) => void
  label?: string
  enabled?: boolean
}

/**
 * The overflow-menu form of `TagPicker`: a checkable submenu of the
 * organization's tags, for use as an `ActionToolbar` custom item's `overflowNode`
 * when the inline tag filter collapses into the `⋯` menu. Native menu items
 * (no nested popover), so it composes cleanly inside the overflow dropdown.
 */
export const TagFilterSubmenu: React.FC<TagFilterSubmenuProps> = ({
  selectedTags,
  onSelectedTagsChange,
  label = "Tags",
  enabled = true,
}) => {
  const { suggestions } = useOrganizationTags({ enabled })
  const items = buildTagFilterItems({ selectedTags, suggestions })
  const selected = new Set(normalizeTags(selectedTags))

  const toggle = (tag: string) => {
    const next = selected.has(tag)
      ? [...selected].filter((t) => t !== tag)
      : [...selected, tag]
    onSelectedTagsChange(normalizeTags(next))
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="[&>svg]:size-3.5">
        <span className="flex items-center gap-2">
          <Tag />
          {label}
          {selectedTags.length > 0 ? ` (${selectedTags.length})` : ""}
        </span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
        {items.length === 0 ? (
          <DropdownMenuItem disabled>No tags</DropdownMenuItem>
        ) : (
          items.map((item) => {
            const isSelected = selected.has(item.tag)
            return (
              <DropdownMenuItem
                key={item.tag}
                onSelect={(event) => {
                  event.preventDefault()
                  toggle(item.tag)
                }}
              >
                <span className="flex w-full items-center gap-2">
                  <span className="flex w-4 justify-center [&>svg]:size-3.5">
                    {isSelected ? <Check /> : null}
                  </span>
                  {item.tag}
                </span>
              </DropdownMenuItem>
            )
          })
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}
